// ============================================================================
// RAID RESOLVE
//
// One component, two placements. On a card it is a "Resolve" button that
// expands into a two-line form; in the modal it is a permanent section. The
// `compact` prop switches between them so the closing-out flow behaves the
// same wherever it is used.
//
// Status options are relabelled per type via statusLabel, so a question closes
// as Answered and a risk as Mitigated, while one canonical value is stored.
// ============================================================================

import React, { useState } from 'react';
import { Check, X, CheckCircle2 } from 'lucide-react';
import { RaidStatus, RaidType } from '../../../types/raid';
import { RAID_STATUSES, statusLabel } from './raidConfig';

export interface RaidResolveInlineProps {
  type: RaidType;
  currentStatus: RaidStatus;
  currentResolution?: string;
  compact?: boolean;
  saving?: boolean;
  onResolve: (status: RaidStatus, resolution: string) => void;
  onCancel?: () => void;
}

export function RaidResolveInline({
  type,
  currentStatus,
  currentResolution,
  compact = false,
  saving = false,
  onResolve,
  onCancel,
}: Readonly<RaidResolveInlineProps>) {
  const [status, setStatus] = useState<RaidStatus>(
    currentStatus === 'open' ? 'resolved' : currentStatus
  );
  const [resolution, setResolution] = useState(currentResolution ?? '');

  const selectClasses =
    'border border-[#e2e8f0] rounded-xl px-2.5 py-1.5 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white';
  const inputClasses =
    'flex-1 border border-[#e2e8f0] rounded-xl px-3 py-1.5 text-xs text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';

  const body = (
    <div className={compact ? 'flex flex-wrap items-center gap-2' : 'flex flex-col gap-2'}>
      <select
        value={status}
        onChange={e => setStatus(e.target.value as RaidStatus)}
        aria-label="Outcome"
        className={compact ? selectClasses : `${selectClasses} w-full`}
      >
        {RAID_STATUSES.filter(s => s !== 'open').map(s => (
          <option key={s} value={s}>{statusLabel(type, s)}</option>
        ))}
      </select>
      <input
        value={resolution}
        onChange={e => setResolution(e.target.value)}
        placeholder={placeholderFor(status)}
        aria-label="Resolution"
        className={compact ? inputClasses : `${inputClasses} w-full`}
      />
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onResolve(status, resolution.trim())}
          disabled={saving}
          className="flex items-center gap-1 rounded-xl px-3 py-1.5 bg-primary hover:bg-primary-dark text-white text-xs font-bold transition-colors disabled:opacity-60"
        >
          <Check size={12} /> Close out
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
          >
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );

  if (compact) return body;

  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <CheckCircle2 size={11} />Close out
      </label>
      {body}
    </div>
  );
}

function placeholderFor(status: RaidStatus): string {
  switch (status) {
    case 'resolved': return 'What was the answer or outcome?';
    case 'invalidated': return 'What actually happened?';
    case 'deferred': return 'Why is it parked, and until when?';
    default: return 'Why is no action being taken?';
  }
}
